import { marketingBaseShare } from './marketingModel';
import {
  IMarketingCohortRow,
  IMarketingFilter,
  IMarketingOverlapCell,
} from './types';

/**
 * ТЗ 0103 — «Лиды и продажи › Когорты» (design-erp §3.2): накопленная
 * конверсия когорт за 7 / 14 / 30 / 60 / 90 дней, разбивка по источнику,
 * кривые «как быстро дозревают» и «каналы вместе». Модуль чистый: подписи —
 * ключами, числа — долями 0..1.
 *
 * Незрелая клетка (когорта ещё не прожила N дней) в долю не входит ни
 * числителем, ни знаменателем: иначе молодые когорты тянули бы конверсию
 * вниз и «падение» было бы просто возрастом.
 */

/** Горизонты когорт, дней от входа. */
export const MARKETING_COHORT_HORIZONS = [7, 14, 30, 60, 90] as const;

export type MarketingCohortHorizon = typeof MARKETING_COHORT_HORIZONS[number];

/** Горизонт линии «конверсия по источникам». */
export const MARKETING_COHORT_LINE_HORIZON: MarketingCohortHorizon = 30;

/** Вид пути лида (`pathKind`): один канал или несколько. */
export const MARKETING_PATH_KINDS = ['single', 'multi'] as const;

export type MarketingPathKind = typeof MARKETING_PATH_KINDS[number];

export const isMarketingPathKind = (
  value: string | null | undefined,
): value is MarketingPathKind =>
  Boolean(value) &&
  (MARKETING_PATH_KINDS as readonly string[]).includes(value as string);

/**
 * Матрицу когорт (`marketingCohorts`) сервер строит из `CohortFact` с
 * зерном «неделя × канал × семейство × вид пути»: оффера, кампании, группы
 * объявлений и объявления в нём нет, и такие фильтры её не сужают (многоканальные
 * когорты и «каналы вместе» той же подвкладки их соблюдают). Экран говорит
 * об этом словами, а не показывает «отфильтрованные» цифры.
 */
export const marketingCohortsIgnoreFilter = (
  filter: IMarketingFilter,
): boolean =>
  Boolean(filter.offers?.length) ||
  Boolean(filter.campaignIds?.length) ||
  Boolean(filter.adsetIds?.length) ||
  Boolean(filter.adIds?.length);

export interface IMarketingCohortCell {
  /** Продажи за горизонт; поля нет в ответе — `null`. */
  sales: number | null;
  /** Когорта прожила горизонт целиком — цифра окончательная. */
  mature: boolean;
}

/** Продажи когорты за горизонт и зрелость. */
export const marketingCohortCell = (
  row: IMarketingCohortRow,
  days: MarketingCohortHorizon,
): IMarketingCohortCell => {
  switch (days) {
    case 7:
      return { sales: row.sales7 ?? null, mature: Boolean(row.mature7) };
    case 14:
      return { sales: row.sales14 ?? null, mature: Boolean(row.mature14) };
    case 30:
      return { sales: row.sales30, mature: row.mature30 };
    case 60:
      return { sales: row.sales60, mature: row.mature60 };
    case 90:
    default:
      return { sales: row.sales90, mature: row.mature90 };
  }
};

export interface IMarketingCohortHorizonSum {
  /** Продажи зрелых когорт за горизонт. */
  sales: number;
  /** Лиды зрелых когорт — знаменатель доли. */
  leads: number;
  /** Доля; база меньше 30 лидов — `null` («мало данных»). */
  share: number | null;
}

export interface IMarketingCohortGroup {
  /** Значение разбивки (`split`); без разбивки — `all`. */
  key: string;
  /** Подпись сервера (`splitLabel`), если она есть. */
  label: string | null;
  /** Лиды всех когорт группы в окне. */
  leads: number;
  horizons: Record<MarketingCohortHorizon, IMarketingCohortHorizonSum>;
}

/** Ключ группы без разбивки. */
export const MARKETING_COHORT_ALL = 'all';

/**
 * Группы разбивки по всем когортам окна: на каждый горизонт — продажи и
 * лиды только ЗРЕЛЫХ когорт. Порядок — по лидам ↓, при равенстве — ключ.
 */
export const marketingCohortGroups = (
  rows: ReadonlyArray<IMarketingCohortRow>,
): IMarketingCohortGroup[] => {
  const groups = new Map<string, IMarketingCohortGroup>();
  rows.forEach((row) => {
    const key = row.split ?? MARKETING_COHORT_ALL;
    let group = groups.get(key);
    if (!group) {
      group = {
        key,
        label: row.splitLabel ?? null,
        leads: 0,
        horizons: MARKETING_COHORT_HORIZONS.reduce(
          (acc, days) => ({
            ...acc,
            [days]: { sales: 0, leads: 0, share: null },
          }),
          {} as Record<MarketingCohortHorizon, IMarketingCohortHorizonSum>,
        ),
      };
      groups.set(key, group);
    }
    group.leads += row.leads;
    MARKETING_COHORT_HORIZONS.forEach((days) => {
      const cell = marketingCohortCell(row, days);
      if (!cell.mature || cell.sales === null) return;
      const sum = (group as IMarketingCohortGroup).horizons[days];
      sum.sales += cell.sales;
      sum.leads += row.leads;
    });
  });
  const list = Array.from(groups.values());
  list.forEach((group) =>
    MARKETING_COHORT_HORIZONS.forEach((days) => {
      const sum = group.horizons[days];
      sum.share =
        sum.leads > 0 ? marketingBaseShare(sum.sales, sum.leads) : null;
    }),
  );
  return list.sort((a, b) => b.leads - a.leads || a.key.localeCompare(b.key));
};

/** Когорты окна по порядку (недели/месяцы), без повторов. */
export const marketingCohortKeys = (
  rows: ReadonlyArray<IMarketingCohortRow>,
): string[] =>
  Array.from(new Set(rows.map((row) => row.cohort))).sort((a, b) =>
    a.localeCompare(b),
  );

export interface IMarketingCohortLine {
  key: string;
  label: string | null;
  /** По когортам `marketingCohortKeys`; незрелая или без лидов — `null`. */
  values: Array<number | null>;
}

/**
 * «Конверсия за N дней по источникам»: линия на значение разбивки, точка —
 * доля продаж когорты за горизонт. Незрелая когорта и когорта без лидов —
 * `null` (разрыв, а не ноль).
 */
export const marketingCohortLines = (
  rows: ReadonlyArray<IMarketingCohortRow>,
  days: MarketingCohortHorizon = MARKETING_COHORT_LINE_HORIZON,
): { cohorts: string[]; lines: IMarketingCohortLine[] } => {
  const cohorts = marketingCohortKeys(rows);
  const groups = marketingCohortGroups(rows);
  const byKey = new Map(
    rows.map((row) => [
      `${row.split ?? MARKETING_COHORT_ALL}|${row.cohort}`,
      row,
    ]),
  );
  const lines = groups.map((group) => ({
    key: group.key,
    label: group.label,
    values: cohorts.map((cohort) => {
      const row = byKey.get(`${group.key}|${cohort}`);
      if (!row || row.leads <= 0) return null;
      const cell = marketingCohortCell(row, days);
      return cell.mature && cell.sales !== null ? cell.sales / row.leads : null;
    }),
  }));
  return { cohorts, lines };
};

/**
 * «Как быстро дозревают»: накопленная доля продаж на днях 7…90 по группе —
 * по зрелым когортам каждого горизонта; база меньше 30 — `null`.
 */
export const marketingCohortCurves = (
  groups: ReadonlyArray<IMarketingCohortGroup>,
): IMarketingCohortLine[] =>
  groups.map((group) => ({
    key: group.key,
    label: group.label,
    values: MARKETING_COHORT_HORIZONS.map((days) => group.horizons[days].share),
  }));

/* ── «Каналы вместе» ─────────────────────────────────────────────────── */

export interface IMarketingOverlapView {
  /** Каналы по лидам в пути ↓ (диагональ). */
  channels: string[];
  cells: Record<string, IMarketingOverlapCell>;
  /** Наибольшее вне диагонали — пары, а не канал сам с собой. */
  maxPair: number;
}

export const marketingOverlapKey = (row: string, col: string): string =>
  `${row}|${col}`;

/** Ячейки «канал × канал» → матрица (каналы — по лидам диагонали). */
export const marketingOverlapView = (
  cells: ReadonlyArray<IMarketingOverlapCell>,
): IMarketingOverlapView => {
  const diagonal = new Map<string, number>();
  const byKey: Record<string, IMarketingOverlapCell> = {};
  let maxPair = 0;
  cells.forEach((cell) => {
    byKey[marketingOverlapKey(cell.row, cell.col)] = cell;
    if (cell.row === cell.col) diagonal.set(cell.row, cell.leads);
    else maxPair = Math.max(maxPair, cell.leads);
    if (!diagonal.has(cell.row)) diagonal.set(cell.row, 0);
    if (!diagonal.has(cell.col)) diagonal.set(cell.col, 0);
  });
  const channels = Array.from(diagonal.keys()).sort(
    (a, b) =>
      (diagonal.get(b) ?? 0) - (diagonal.get(a) ?? 0) || a.localeCompare(b),
  );
  return { channels, cells: byKey, maxPair };
};
